import { CopyOutlined, DeleteOutlined, SaveFilled } from "@ant-design/icons";
import {
  type IDomEditor,
  type IEditorConfig,
  type IToolbarConfig,
  i18nChangeLanguage,
} from "@wangeditor/editor";
import { Editor, Toolbar } from "@wangeditor/editor-for-react";

import type React from "react";
import { useCallback, useEffect, useRef, useState } from "react";
import "@wangeditor/editor/dist/css/style.css";
import "./RichTextEditor.less";
import { ReloadOutlined } from "@ant-design/icons";
import { Alert, Button, message, Space, Spin, Tooltip } from "antd";
import { cn } from "@/lib";

export interface TemplateEditor {
  id: string;
  name?: string;
  content: string;
}

export interface RichTextEditorProps {
  editor?: TemplateEditor;
  isLocked?: boolean;
  content?: string;
  className?: string;
  onCopy?: (editor: TemplateEditor) => void; // Deprecated: now only copies to clipboard
  onRemove?: (id: string | number) => void; // Deprecated: now only clears content
  onContentChange: (id: string, content: string) => void;
  onFocus?: (id: string | number) => void;
  disableFocus?: boolean; // New prop to disable focus handling
  onLegacyChange?: (id: string | number, content: string) => void; // Renamed to avoid conflict
  isAction?: boolean;
  enableImageUpload?: boolean;
  forceContentSync?: boolean;
  // Standard Ant Design Form control interface
  value?: string;
  onChange?: (value: string) => void;
  // Socket integration for real-time updates
  onSocketUpdate?: (editorId: string, content: string) => void;
  onSave?: () => void;
  isSaving?: boolean;
}

const RichTextEditor: React.FC<RichTextEditorProps> = (props) => {
  const {
    editor: editorProp,
    isLocked = false,
    content,
    onCopy: _onCopy, // Renamed to indicate it's unused
    onRemove: _onRemove, // Renamed to indicate it's unused
    onContentChange,
    onFocus,
    disableFocus = false,
    onLegacyChange,
    className,
    enableImageUpload = true,
    forceContentSync = false,
    // Standard Ant Design Form control props
    value,
    onChange: formOnChange,
    // Socket integration
    onSocketUpdate,
    onSave,
    isSaving,
  } = props;

  const [editor, setEditor] = useState<IDomEditor | null>(null);
  const [hasError, setHasError] = useState(false);
  const [isRecovering, setIsRecovering] = useState(false);
  const [lastValidContent, setLastValidContent] = useState<string>("");
  const editorContainerRef = useRef<HTMLDivElement>(null);
  const suppressChangeRef = useRef(false);
  const editorRef = useRef<IDomEditor | null>(null);
  const initializedRef = useRef(false);
  const editorKeyRef = useRef(0); // Force re-render key
  const disableFocusRef = useRef(disableFocus);
  const lastSetContentRef = useRef<string>(""); // Track last content set by setHtml to avoid loops
  const lastUserInputTimeRef = useRef<number>(0); // Track last user input time to distinguish from external updates

  // Update disableFocus ref when prop changes
  useEffect(() => {
    disableFocusRef.current = disableFocus;
  }, [disableFocus]);

  // Content validation and normalization function
  const validateAndNormalizeContent = useCallback((content: string): string => {
    try {
      // Remove potentially problematic characters and structures
      let normalizedContent = content
        // biome-ignore lint: need control character removal for safety
        .replace(/[\u0000-\u0008\u000B\u000C\u000E-\u001F\u007F]/g, "") // Remove control characters
        .replace(/\r\n/g, "\n") // Normalize line endings
        .replace(/\r/g, "\n")
        .trim();

      // Handle empty content
      if (!normalizedContent || normalizedContent === "<p></p>") {
        return "<p><br></p>";
      }

      // Ensure content has proper paragraph structure
      if (!normalizedContent.startsWith("<") || !normalizedContent.includes("<p")) {
        // Wrap plain text in paragraph
        normalizedContent = `<p>${normalizedContent.replace(/\n/g, "</p><p>")}</p>`;
      }

      // Remove invalid nested structures that could cause path issues
      normalizedContent = normalizedContent
        .replace(/<p><p>/g, "<p>") // Remove nested paragraphs
        .replace(/<\/p><\/p>/g, "</p>") // Remove double closing
        .replace(/<p><\/p>/g, "<p><br></p>"); // Fix empty paragraphs

      // Validate by trying to parse as DOM
      const tempDiv = document.createElement("div");
      tempDiv.innerHTML = normalizedContent;

      // Check for any problematic structures
      const paragraphs = tempDiv.querySelectorAll("p");
      if (paragraphs.length === 0) {
        // If no paragraphs found, wrap in one
        return `<p>${normalizedContent}</p>`;
      }

      return tempDiv.innerHTML;
    } catch (error) {
      console.warn("Content validation failed, using fallback:", error);
      // Fallback to safe default
      return content.trim() ? `<p>${content.replace(/<[^>]*>/g, "").trim()}</p>` : "<p><br></p>";
    }
  }, []);

  // Advanced editor state recovery function
  const recoverEditorState = useCallback(
    async (editorInstance: IDomEditor, targetContent: string): Promise<void> => {
      try {
        // Method 1: Try to clear and rebuild completely
        console.log("Attempting complete editor state recovery...");

        // First, try to reset editor to known good state
        editorInstance.clear();
        await new Promise((resolve) => setTimeout(resolve, 50));

        // For large content, just use setHtml as it's more reliable than fragments
        editorInstance.setHtml(targetContent);

        // Reset selection to start
        editorInstance.selection = {
          anchor: { path: [0, 0], offset: 0 },
          focus: { path: [0, 0], offset: 0 },
        };

        console.log("Editor state recovery successful");
      } catch (error) {
        console.error("Advanced recovery also failed:", error);
        throw error;
      }
    },
    [],
  );

  // Set editor language to English
  useEffect(() => {
    i18nChangeLanguage("en");
  }, []);

  // if editor is not null, update the ref
  useEffect(() => {
    if (editor) {
      editorRef.current = editor;
    }
  }, [editor]);

  // Toolbar configuration - conditionally include image upload
  const toolbarConfig: Partial<IToolbarConfig> = {
    toolbarKeys: [
      "bold",
      "underline",
      "italic",
      "through",
      "header1",
      "header2",
      "header3",
      "insertTable",
      ...(enableImageUpload ? ["uploadImage"] : []),
    ],
  };

  // Editor configuration
  const editorConfig: Partial<IEditorConfig> = {
    placeholder: "Please enter content...",
    autoFocus: false, // Disable auto focus
    MENU_CONF: {
      header1: {
        title: "Heading 1",
      },
      header2: {
        title: "Heading 2",
      },
      header3: {
        title: "Heading 3",
      },
      ...(enableImageUpload && {
        uploadImage: {
          // Enhanced image upload with optional resizing
          customUpload(file: File, insertFn: (url: string, alt: string, href: string) => void) {
            // Check file size - if over 1MB, resize it
            const MAX_FILE_SIZE = 1024 * 1024; // 1MB

            const processImage = (imageFile: File) => {
              const reader = new FileReader();
              reader.readAsDataURL(imageFile);
              reader.onload = () => {
                const url = reader.result as string;
                // insert image
                insertFn(url, imageFile.name, "");

                // Position cursor after image
                positionCursorAfterImage();
              };
            };

            /* Image resizing functionality commented out
            const resizeImage = (imageFile: File) => {
              const img = new Image();
              const reader = new FileReader();

              reader.onload = (e) => {
                img.src = e.target?.result as string;

                img.onload = () => {
                  // Calculate new dimensions while maintaining aspect ratio
                  const MAX_WIDTH = 1200;
                  const MAX_HEIGHT = 1200;

                  let width = img.width;
                  let height = img.height;

                  if (width > MAX_WIDTH) {
                    height = (height * MAX_WIDTH) / width;
                    width = MAX_WIDTH;
                  }

                  if (height > MAX_HEIGHT) {
                    width = (width * MAX_HEIGHT) / height;
                    height = MAX_HEIGHT;
                  }

                  // Create canvas for resizing
                  const canvas = document.createElement("canvas");
                  canvas.width = width;
                  canvas.height = height;

                  // Draw and resize image on canvas
                  const ctx = canvas.getContext("2d");
                  ctx?.drawImage(img, 0, 0, width, height);

                  // Convert to blob with reduced quality
                  canvas.toBlob(
                    (blob) => {
                      if (blob) {
                        const resizedFile = new File([blob], file.name, {
                          type: file.type,
                          lastModified: Date.now(),
                        });
                        processImage(resizedFile);
                      } else {
                        // Fallback to original if resizing fails
                        processImage(file);
                      }
                    },
                    file.type,
                    0.85, // 85% quality
                  );
                };
              };

              reader.readAsDataURL(imageFile);
            };
            */

            // Simple replacement function to process images directly without resizing
            const resizeImage = (imageFile: File) => {
              // Process original image without resizing
              processImage(imageFile);
            };

            const positionCursorAfterImage = () => {
              setTimeout(() => {
                const currentEditor = editorRef.current;
                if (currentEditor && !disableFocusRef.current) {
                  // ensure the editor is focused
                  currentEditor.focus();

                  // try to insert a zero-width space at the current position, then delete it
                  // this trick can ensure the cursor is positioned after the image
                  try {
                    const range = document.getSelection()?.getRangeAt(0);
                    if (range) {
                      // ensure the cursor is positioned after the image
                      const zeroWidthSpace = document.createTextNode("\u200B");
                      range.insertNode(zeroWidthSpace);
                      range.setStartAfter(zeroWidthSpace);
                      range.collapse(true);

                      // re-focus the editor
                      if (!disableFocusRef.current) {
                        currentEditor.focus();
                      }
                    }
                  } catch (_) {
                    // if the above method fails, at least ensure the editor is focused
                    if (!disableFocusRef.current) {
                      currentEditor.focus();
                    }
                  }
                }
              }, 100);
            };

            // Check if file is an image
            if (!file.type.startsWith("image/")) {
              alert("Only image files are allowed");
              return false;
            }

            // Process based on file size
            if (file.size > MAX_FILE_SIZE) {
              resizeImage(file);
            } else {
              processImage(file);
            }

            return true;
          },
        },
      }),
    },
    onFocus: () => {
      if (onFocus && editorProp?.id) {
        onFocus(editorProp.id);
      }
    },
  };

  // Error recovery handler
  const handleEditorError = useCallback(
    (error: Error) => {
      console.error("Editor error detected:", error);
      setHasError(true);

      // Save current content as backup if editor is still accessible
      try {
        if (editor && !editor.isDestroyed) {
          const currentContent = editor.getHtml();
          if (currentContent && currentContent !== "<p><br></p>") {
            setLastValidContent(currentContent);
          }
        }
      } catch (e) {
        console.warn("Failed to save current content during error:", e);
      }
    },
    [editor],
  );

  // Recover editor state
  const handleRecoverEditor = useCallback(() => {
    setIsRecovering(true);
    setHasError(false);

    // Increment key to force complete re-render
    editorKeyRef.current += 1;

    // Reset state
    setEditor(null);
    initializedRef.current = false;
    suppressChangeRef.current = false;

    // Clear recovery state after a delay
    setTimeout(() => {
      setIsRecovering(false);
    }, 1000);
  }, []);

  const handleEditorCreated = useCallback(
    (createdEditor: IDomEditor) => {
      try {
        setEditor(createdEditor);

        // Set up enhanced error monitoring and safe operations
        const originalSetHtml = createdEditor.setHtml.bind(createdEditor);
        const originalClear = createdEditor.clear.bind(createdEditor);
        const originalInsertFragment = createdEditor.insertFragment?.bind(createdEditor);

        // Enhanced setHtml with validation and recovery
        createdEditor.setHtml = (html: string) => {
          try {
            // Validate content before setting
            const validatedContent = validateAndNormalizeContent(html);

            // Check editor state consistency before operation
            if (createdEditor.children && createdEditor.selection) {
              const maxPath = createdEditor.children.length - 1;
              const currentSelection = createdEditor.selection;

              // Fix invalid selection paths
              if (currentSelection.anchor && currentSelection.anchor.path[0] > maxPath) {
                console.warn("Fixing invalid selection path before setHtml");
                createdEditor.selection = {
                  anchor: { path: [0, 0], offset: 0 },
                  focus: { path: [0, 0], offset: 0 },
                };
              }
            }

            return originalSetHtml(validatedContent);
          } catch (error) {
            console.error("Error in enhanced setHtml:", error);

            // Try recovery
            try {
              const fallbackContent = html.trim()
                ? `<p>${html.replace(/<[^>]*>/g, "").trim()}</p>`
                : "<p><br></p>";
              return originalSetHtml(fallbackContent);
            } catch (fallbackError) {
              console.error("Fallback setHtml also failed:", fallbackError);
              handleEditorError(error as Error);
              throw error;
            }
          }
        };

        // Enhanced clear operation
        createdEditor.clear = () => {
          try {
            return originalClear();
          } catch (error) {
            console.error("Error in clear:", error);
            // Try manual reset
            try {
              // Reset to empty state manually
              originalSetHtml("<p><br></p>");
            } catch (manualError) {
              console.error("Manual clear also failed:", manualError);
              handleEditorError(error as Error);
            }
          }
        };

        // Enhanced insertFragment if available
        if (originalInsertFragment) {
          createdEditor.insertFragment = (fragment: unknown) => {
            try {
              // biome-ignore lint/suspicious/noExplicitAny: Slate.js fragment types are complex
              return originalInsertFragment(fragment as any);
            } catch (error) {
              console.error("Error in insertFragment:", error);
              // Try to insert as plain text instead
              try {
                const textContent = typeof fragment === "string" ? fragment : String(fragment);
                createdEditor.insertText(textContent);
              } catch (textError) {
                console.error("Text insertion also failed:", textError);
                handleEditorError(error as Error);
              }
            }
          };
        }

        // Save valid content periodically
        const saveValidContent = () => {
          try {
            if (!createdEditor.isDestroyed) {
              const content = createdEditor.getHtml();
              if (content && content !== "<p><br></p>" && content.trim().length > 0) {
                setLastValidContent(content);
              }
            }
          } catch {
            // Ignore errors during content saving
          }
        };

        // Set up periodic content saving
        const saveInterval = setInterval(saveValidContent, 5000);

        // Clean up interval when editor is destroyed
        createdEditor.on("destroyed", () => {
          clearInterval(saveInterval);
        });

        if (onFocus && editorProp?.id) {
          createdEditor.on("focus", () => {
            // Check disableFocus dynamically using ref
            if (!disableFocusRef.current) {
              onFocus(editorProp.id);
            }
          });
        }

        // If we have last valid content and no current content, restore it
        if (lastValidContent && lastValidContent.trim().length > 0) {
          setTimeout(() => {
            try {
              const currentContent = createdEditor.getHtml();
              if (
                !currentContent ||
                currentContent === "<p><br></p>" ||
                currentContent.trim().length === 0
              ) {
                suppressChangeRef.current = true;
                createdEditor.setHtml(lastValidContent);
                setTimeout(() => {
                  suppressChangeRef.current = false;
                }, 50);

                // Notify about content restoration
                if (onContentChange && editorProp?.id) {
                  onContentChange(String(editorProp.id), lastValidContent);
                }
              }
            } catch (restoreError) {
              console.warn("Failed to restore content after recovery:", restoreError);
            }
          }, 100);
        }
      } catch (error) {
        console.error("Error in handleEditorCreated:", error);
        handleEditorError(error as Error);
      }
    },
    [
      onFocus,
      editorProp?.id,
      handleEditorError,
      lastValidContent,
      onContentChange,
      validateAndNormalizeContent,
    ],
  );

  useEffect(() => {
    return () => {
      if (editor == null) return;
      editor.destroy();
      setEditor(null);
    };
  }, [editor]);

  useEffect(() => {
    if (isLocked) {
      editor?.disable();
    } else {
      editor?.enable();
    }
  }, [isLocked, editor]);

  // Enhanced content synchronization with advanced validation and state normalization
  useEffect(() => {
    if (hasError) {
      return; // Skip content sync during error state
    }

    // Use value prop (from form) first, then fall back to content prop or editorProp.content
    const effectiveContent =
      value !== undefined ? value : content !== undefined ? content : editorProp?.content;

    // Only sync content if a content source is provided and differs from current editor content
    if (editor && effectiveContent !== undefined && !editor.isDestroyed) {
      try {
        const currentContent = editor.getHtml();

        if (effectiveContent !== currentContent) {
          // Check if this is the same content we just set (avoid loop)
          if (effectiveContent === lastSetContentRef.current) {
            return;
          }

          // Only sync content if:
          // 1. It's the initial setup (!initializedRef.current), OR
          // 2. The new content is significantly longer (likely template insertion), OR
          // 3. The current content is empty (editor reset), OR
          // 4. Current content is the default empty state and we have real content to set
          // 5. Content change happened outside user input time window (external update)
          const isInitialSetup = !initializedRef.current;

          // CRITICAL FIX: Lower threshold from 50 to 20 to catch shorter templates
          // This fixes Journal Tab template insertion for short templates
          const isTemplateInsertion = effectiveContent.length > currentContent.length + 20;

          const isEditorEmpty = currentContent.trim().length === 0;
          const isDefaultEmpty =
            currentContent === "<p><br></p>" &&
            effectiveContent !== "<p><br></p>" &&
            effectiveContent.trim() !== "";

          // Check if this update is happening shortly after user input
          // If yes, it's likely a loop from user input -> state update -> prop change
          // If no, it's likely an external update (template insertion, visit switch, etc.)
          const timeSinceLastUserInput = Date.now() - lastUserInputTimeRef.current;
          const isLikelyExternalUpdate = timeSinceLastUserInput > 150; // 150ms window

          // KEY CHANGE: Add isLikelyExternalUpdate to allow external updates
          // This allows template insertions and visit switches while preventing user input loops
          const shouldSyncContent =
            forceContentSync ||
            isInitialSetup ||
            isTemplateInsertion ||
            isEditorEmpty ||
            isDefaultEmpty ||
            (isLikelyExternalUpdate && effectiveContent.length > currentContent.length);

          if (shouldSyncContent) {
            // Suppress change events during content sync to prevent triggering update events
            // This is especially important during visit switching - we only want to initialize data
            suppressChangeRef.current = true;

            // Advanced content validation and normalization
            const safeContent = validateAndNormalizeContent(effectiveContent);

            // Safely set HTML content with enhanced error handling
            const setContentSafely = async () => {
              try {
                // For template insertion, only clear if necessary
                const isTemplateInsertion = effectiveContent.length > currentContent.length + 20;

                if (
                  isInitialSetup ||
                  isEditorEmpty ||
                  (isTemplateInsertion && currentContent === "<p><br></p>")
                ) {
                  // Safe to use setHtml for initial setup, empty editor, or default empty state
                  editor.setHtml(safeContent);
                } else if (isTemplateInsertion) {
                  // For template insertion on existing content, avoid clearing
                  // Just set the HTML directly - the content should already be properly formatted
                  editor.setHtml(safeContent);
                } else {
                  // For other cases, clear first to prevent conflicts
                  editor.clear();
                  await new Promise((resolve) => setTimeout(resolve, 10));
                  editor.setHtml(safeContent);
                }

                // Set sync timestamp to prevent change callback loops
                (window as unknown as { lastContentSyncTime: number }).lastContentSyncTime =
                  Date.now();

                // Track the content we just set to prevent sync loops
                lastSetContentRef.current = safeContent;

                // Save this as valid content
                if (safeContent && safeContent !== "<p><br></p>" && safeContent.trim().length > 0) {
                  setLastValidContent(safeContent);
                }
              } catch (error) {
                console.error("Error setting editor HTML:", error);

                // Try alternative recovery methods
                try {
                  await recoverEditorState(editor, safeContent);
                } catch (recoveryError) {
                  console.error("Failed to recover editor state:", recoveryError);
                  handleEditorError(error as Error);
                  return;
                }
              }
            };

            // Execute async operation
            setContentSafely();

            // Shorter timeout to re-enable change events quickly for user interactions
            // KEY CHANGE: Reduce timeout to 100ms for faster user input response
            // The lastSetContentRef and improved sync logic prevent loops
            const timeout = 100;

            setTimeout(() => {
              suppressChangeRef.current = false;
              // Clear lastSetContentRef after a delay to allow fresh prop updates
              setTimeout(() => {
                lastSetContentRef.current = "";
              }, 200);
            }, timeout);
          }
        }

        // Mark as initialized after first content setting
        if (!initializedRef.current) {
          initializedRef.current = true;
        }
      } catch (error) {
        console.error("Error in content synchronization:", error);
        handleEditorError(error as Error);
      }
    } else if (editor && effectiveContent === undefined && !initializedRef.current) {
      // If no content source is provided, mark as initialized immediately
      initializedRef.current = true;
    }
  }, [
    content,
    value,
    editor,
    hasError,
    handleEditorError,
    validateAndNormalizeContent,
    recoverEditorState,
    editorProp?.content,
    forceContentSync,
  ]);

  const handleChange = (editor: IDomEditor) => {
    try {
      if (suppressChangeRef.current) {
        return;
      }

      // additional lock check protection
      if (isLocked) {
        return;
      }

      const newHtml = editor.getHtml();

      // Track user input time to distinguish from external updates
      lastUserInputTimeRef.current = Date.now();

      // Update lastSetContentRef to track user's latest content
      // This prevents the content sync from overwriting user input
      lastSetContentRef.current = newHtml;

      // Save valid content
      if (newHtml && newHtml !== "<p><br></p>" && newHtml.trim().length > 0) {
        setLastValidContent(newHtml);
      }

      // Priority order for content change handling:
      // 1. Call the standard Ant Design form onChange callback first (most important for form integration)
      // 2. Then call our custom onContentChange callback
      // 3. Finally call any legacy callbacks

      // Call the standard form onChange callback for Ant Design integration FIRST
      // This ensures the form field value is updated and triggers onValuesChange
      if (formOnChange) {
        formOnChange(newHtml);
      } else {
      }

      // Then call our custom content change callback (for template editor updates)
      // Skip this if we're within 500ms of a content sync to prevent loops
      const lastSyncTime =
        (window as unknown as { lastContentSyncTime?: number }).lastContentSyncTime || 0;
      const currentTime = Date.now();
      const timeSinceLastSync = currentTime - lastSyncTime;
      const isWithinSyncWindow = timeSinceLastSync < 500;

      // Always call onContentChange for content tracking, but skip form updates for template editors
      if (editorProp?.id && onContentChange && !isWithinSyncWindow) {
        onContentChange(String(editorProp.id), newHtml);

        if (onLegacyChange && editorProp?.id) {
          onLegacyChange(editorProp.id, newHtml);
        }
      }

      // Trigger socket update if callback is provided (now deprecated in favor of form-based updates)
      if (onSocketUpdate && editorProp?.id) {
        onSocketUpdate(String(editorProp.id), newHtml);
      }
    } catch (error) {
      console.error("Error in handleChange:", error);
      handleEditorError(error as Error);
    }
  };

  const handleCopy = () => {
    if (editor) {
      // Get the HTML content from editor
      const htmlContent = editor.getHtml();

      // Use the modern Clipboard API with HTML support
      // This approach supports both text and HTML with images
      try {
        // Create a Blob containing the HTML content
        const blob = new Blob([htmlContent], { type: "text/html" });

        // Create a ClipboardItem with both HTML and text formats
        const data = new ClipboardItem({
          "text/html": blob,
          "text/plain": new Blob([editor.getText()], { type: "text/plain" }),
        });

        // Use the clipboard.write API to write both formats
        navigator.clipboard
          .write([data])
          .then(() => {
            console.log("Content with images copied to clipboard successfully");
            message.success("Content copied successfully");
          })
          .catch((err) => {
            console.error("Clipboard write failed:", err);
            // Try alternative approach
            copyUsingIframe(htmlContent);
          });
      } catch (err) {
        console.error("Modern clipboard API failed:", err);
        // Try alternative approach
        copyUsingIframe(htmlContent);
      }
    }
  };

  // Alternative approach using iframe
  const copyUsingIframe = (htmlContent: string) => {
    try {
      // Create an invisible iframe
      const iframe = document.createElement("iframe");
      iframe.style.position = "absolute";
      iframe.style.top = "-9999px";
      document.body.appendChild(iframe);

      // Write the HTML content to the iframe
      if (iframe.contentDocument) {
        iframe.contentDocument.open();
        iframe.contentDocument.write(htmlContent);
        iframe.contentDocument.close();
      }

      // Select all content in the iframe
      if (iframe.contentDocument) {
        iframe.contentDocument.designMode = "on";
      }
      iframe.contentWindow?.focus();

      // Execute commands only if contentDocument exists
      let successful = false;
      if (iframe.contentDocument) {
        iframe.contentDocument.execCommand("selectAll", false);
        successful = iframe.contentDocument.execCommand("copy", false);
      }

      // Remove the iframe
      document.body.removeChild(iframe);

      if (successful) {
        console.log("Content copied using iframe method");
        message.success("Content copied successfully");
      } else {
        console.error("Iframe copy method failed");
        // Last resort: just copy as plain text
        navigator.clipboard.writeText(editor?.getText() || "").then(() => {
          message.success("Content copied successfully");
        });
      }
    } catch (err) {
      console.error("All copy methods failed:", err);
      // Last resort: just copy as plain text
      navigator.clipboard.writeText(editor?.getText() || "").then(() => {
        message.success("Content copied successfully");
      });
    }
  };

  const handleRemove = () => {
    if (editor) {
      // Clear editor content
      editor.setHtml("");

      // Notify content change via callbacks
      if (editorProp?.id && onContentChange) {
        onContentChange(String(editorProp.id), "");

        // Call onLegacyChange callback if provided
        if (onLegacyChange) {
          onLegacyChange(editorProp.id, "");
        }
      }

      // Show success message
      message.success("Content deleted successfully");

      // For delete operation, we only clear content
      // Don't call onRemove callback to avoid deleting the entire editor
    }
  };

  // Error state UI
  if (hasError) {
    return (
      <div
        className={cn("rich-text-container", "rich-text-error", className)}
        ref={editorContainerRef}
        data-editor-id={editorProp?.id || ""}
      >
        <Alert
          message="Editor Error"
          description={
            <div>
              <p>
                The editor encountered an unexpected error, but your content has been automatically
                saved.
              </p>
              {lastValidContent && (
                <p>
                  <small>
                    Last saved content length: {lastValidContent.replace(/<[^>]*>/g, "").length}{" "}
                    characters
                  </small>
                </p>
              )}
            </div>
          }
          type="warning"
          action={
            <Button
              size="small"
              icon={<ReloadOutlined />}
              onClick={handleRecoverEditor}
              loading={isRecovering}
            >
              Recover Editor
            </Button>
          }
          showIcon
        />
      </div>
    );
  }

  // Recovery state UI
  if (isRecovering) {
    return (
      <div
        className={cn("rich-text-container", "rich-text-recovering", className)}
        ref={editorContainerRef}
        data-editor-id={editorProp?.id || ""}
      >
        <div style={{ textAlign: "center", padding: "60px 20px" }}>
          <Spin size="large" />
          <p style={{ marginTop: 16, color: "#666" }}>Recovering editor...</p>
        </div>
      </div>
    );
  }

  return (
    <div
      className={cn("rich-text-container", { "rich-text-container-locked": isLocked }, className)}
      ref={editorContainerRef}
      data-editor-id={editorProp?.id || ""}
      key={`editor-${editorKeyRef.current}`}
    >
      {/* {editorProp?.name ? <span className="template-editor-title">{editorProp.name}</span> : null} */}
      <div className="editor-wrapper">
        <Toolbar
          mode="default"
          editor={editor}
          defaultConfig={toolbarConfig}
          className="custom-toolbar !bg-[#F7F9FC] mx-[9px] mt-[8px] rounded-1"
        />
        <div
          className="editor-content-wrapper"
          onClick={() => {
            // Focus editor when clicking anywhere in the content area
            if (editor && !editor.isDestroyed && !isLocked) {
              editor.focus();
            }
          }}
        >
          <Editor
            mode="simple"
            defaultHtml={editorProp?.content || "<p><br></p>"}
            onCreated={handleEditorCreated}
            defaultConfig={editorConfig}
            onChange={handleChange}
            style={{ minHeight: "300px", overflowY: "hidden" }}
          />
          {/* {editorProp && isAction && onCopy && onRemove && ( */}
          <div className="floating-actions">
            <Space>
              {onSave && (
                <Tooltip title={isLocked ? "Cannot save in locked state" : "Save content"}>
                  <Button
                    type="primary"
                    size="large"
                    className={`save-btn ${isLocked || isSaving ? "disabled" : ""}`}
                    onClick={isLocked || isSaving ? undefined : onSave}
                    disabled={isLocked || isSaving}
                    icon={isSaving ? <Spin size="small" /> : <SaveFilled />}
                    aria-label="Save"
                  >
                    Save
                  </Button>
                </Tooltip>
              )}
              <Tooltip title={isLocked ? "Cannot copy in locked state" : "Copy all content"}>
                <Button
                  type="text"
                  className={`floating-action-btn copy-btn ${isLocked ? "disabled" : ""}`}
                  onClick={isLocked ? undefined : handleCopy}
                  disabled={isLocked}
                  icon={<CopyOutlined />}
                  aria-label="Copy"
                />
              </Tooltip>
              <Tooltip title={isLocked ? "Cannot delete in locked state" : "Delete all content"}>
                <Button
                  type="text"
                  className={`floating-action-btn delete-btn ${isLocked ? "disabled" : ""}`}
                  onClick={isLocked ? undefined : handleRemove}
                  disabled={isLocked}
                  icon={<DeleteOutlined />}
                  aria-label="Delete"
                />
              </Tooltip>
            </Space>
          </div>
          {/* )} */}
        </div>
      </div>
    </div>
  );
};

export default RichTextEditor;
